import { Router } from "express";

import { assignmentController } from "../controllers/assignment-controller";
import {
  authenticate,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import { requireRole } from "../middleware/role-middleware";
import {
  attachSchoolContext,
  requireSchoolContext,
} from "../middleware/school-context-middleware";
import { uploadAssignmentMaterial } from "../middleware/upload-middleware";
import {
  createAssignmentValidation,
  gradeAssignmentValidation,
  updateAssignmentValidation,
} from "../validation/assignment-validation";

const router = Router();

router.use(authenticate);
router.use(requireEmailVerification);
router.use(requireRole(["school", "teacher"]));
router.use(attachSchoolContext);

/**
 * @route   POST /api/v1/school/assignments
 * @desc    Create a new assignment (with optional materialFile upload)
 * @access  Private (School Admin, Teacher)
 */
router.post(
  "/",
  requireSchoolContext,
  uploadAssignmentMaterial,
  async (req, res) => {
    const { error } = createAssignmentValidation.validate(req.body);
    if (error) {
      return res.status(400).json({
        success: false,
        message: "Validation failed",
        error: error.details[0].message,
      });
    }
    return assignmentController.createAssignment(req, res);
  },
);

/**
 * @route   GET /api/v1/school/assignments
 * @desc    Get all assignments for school (filter by class/semester)
 * @access  Private (School Admin, Teacher)
 * @query   classId, semesterId, status
 */
router.get("/", requireSchoolContext, (req, res) =>
  assignmentController.getAllAssignments(req, res),
);

/**
 * @route   GET /api/v1/school/assignments/:id
 * @desc    Get assignment by ID
 * @access  Private (School Admin, Teacher)
 */
router.get("/:id", requireSchoolContext, (req, res) =>
  assignmentController.getAssignmentById(req, res),
);

/**
 * @route   PUT /api/v1/school/assignments/:id
 * @desc    Update assignment (with optional materialFile upload)
 * @access  Private (School Admin, Teacher)
 */
router.put(
  "/:id",
  requireSchoolContext,
  uploadAssignmentMaterial,
  async (req, res) => {
    const { error } = updateAssignmentValidation.validate(req.body);
    if (error) {
      return res.status(400).json({
        success: false,
        message: "Validation failed",
        error: error.details[0].message,
      });
    }
    return assignmentController.updateAssignment(req, res);
  },
);

/**
 * @route   DELETE /api/v1/school/assignments/:id
 * @desc    Delete assignment
 * @access  Private (School Admin, Teacher)
 */
router.delete("/:id", requireSchoolContext, (req, res) =>
  assignmentController.deleteAssignment(req, res),
);

/**
 * @route   POST /api/v1/school/assignments/:id/publish
 * @desc    Publish assignment
 * @access  Private (School Admin, Teacher)
 */
router.post("/:id/publish", requireSchoolContext, (req, res) =>
  assignmentController.publishAssignment(req, res),
);

/**
 * @route   POST /api/v1/school/assignments/:id/close
 * @desc    Close assignment (no more submissions)
 * @access  Private (School Admin, Teacher)
 */
router.post("/:id/close", requireSchoolContext, (req, res) =>
  assignmentController.closeAssignment(req, res),
);

/**
 * @route   GET /api/v1/school/assignments/:id/submissions
 * @desc    Get all submissions for an assignment
 * @access  Private (School Admin, Teacher)
 * @query   status
 */
router.get("/:id/submissions", requireSchoolContext, (req, res) =>
  assignmentController.getAssignmentSubmissions(req, res),
);

/**
 * @route   PUT /api/v1/school/assignments/:id/submissions/:submissionId/grade
 * @desc    Grade assignment submission
 * @access  Private (School Admin, Teacher)
 */
router.put(
  "/:id/submissions/:submissionId/grade",
  requireSchoolContext,
  async (req, res) => {
    const { error } = gradeAssignmentValidation.validate(req.body);
    if (error) {
      return res.status(400).json({
        success: false,
        message: "Validation failed",
        error: error.details[0].message,
      });
    }
    return assignmentController.gradeSubmission(req, res);
  },
);

export default router;
